import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MoreHorizontal, Plus, Search, Trash, Key } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { ConfirmDeleteDialog } from "./components/ConfirmDeleteDialog";
import { AddClientDialog } from "./components/AddClientDialog";
import { ClientVisibilityDialog } from "./components/ClientVisibilityDialog";
import { useRealtimeClients } from "@/hooks/use-realtime-clients";

type Client = {
  id: string;
  name: string;
  logoInitials: string;
  logo?: string | null;
  status: "active" | "inactive" | "pending";
  socialAccounts: number;
  pendingTasks: number;
  nextDelivery?: string;
  user_id?: string;
  company_id?: string;
  visible_to?: string[];
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { clients: realtimeClients, isLoading, refreshClients } = useRealtimeClients();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Transform realtime clients to match the Client type
  const clients = realtimeClients.map(client => ({
    id: client.id,
    name: client.name,
    logoInitials: client.logo_initials || client.name.substring(0, 2).toUpperCase(),
    logo: client.logo_url,
    status: (client.status as "active" | "inactive" | "pending") || "active",
    socialAccounts: 0, // Default value since it's not in the database
    pendingTasks: 0, // Default value since it's not in the database
    nextDelivery: client.next_delivery,
    user_id: client.user_id,
    company_id: client.company_id,
    visible_to: Array.isArray(client.visible_to) ? client.visible_to.map(id => String(id)) : []
  }));

  const handleDeleteClient = async (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("clients")
        .delete()
        .eq("id", clientToDelete.id);
      if (error) throw error;
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      toast.success("Cliente excluído com sucesso!");
      refreshClients();
    } catch (err: any) {
      console.error("Error deleting client:", err);
      toast.error("Erro ao excluir cliente: " + (err?.message || ""));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (client: Client, newStatus: "active" | "inactive") => {
    try {
      const { error } = await supabase
        .from("clients")
        .update({ status: newStatus })
        .eq("id", client.id);
      
      if (error) throw error;
      
      toast.success(`Cliente ${newStatus === "active" ? "ativado" : "desativado"} com sucesso!`);
      refreshClients();
    } catch (err: any) {
      console.error("Error updating client status:", err);
      toast.error("Erro ao atualizar status do cliente: " + (err?.message || ""));
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: Client["status"]) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500 hover:bg-green-600">Ativo</Badge>;
      case "inactive":
        return <Badge variant="outline" className="text-muted-foreground">Inativo</Badge>;
      case "pending":
        return <Badge variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-white">Pendente</Badge>;
      default:
        return null;
    }
  };
  
  const handleAddClient = (newClient: any) => {
    console.log("Cliente adicionado, atualizando lista");
    refreshClients();
    setActiveTab("all");
  };
  
  const handleClientClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const renderClientCard = (client: Client) => (
    <div
      key={client.id}
      className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 transition-colors"
    >
      <div 
        className="flex items-center space-x-4 cursor-pointer flex-1" 
        onClick={() => handleClientClick(client.id)}
      >
        <Avatar className="h-12 w-12">
          {client.logo ? (
            <AvatarImage src={client.logo} alt={client.name} />
          ) : (
            <AvatarFallback className="bg-[#0448FF] text-white">
              {client.logoInitials}
            </AvatarFallback>
          )}
        </Avatar>
        <div>
          <div className="font-medium">{client.name}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {client.socialAccounts} contas • {client.pendingTasks} tarefas pendentes
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        {client.nextDelivery && (
          <div className="hidden md:block text-sm">
            <div className="text-muted-foreground">Próxima entrega:</div>
            <div>{client.nextDelivery}</div>
          </div>
        )}

        {/* Botão de compartilhamento */}
        <ClientVisibilityDialog
          clientId={client.id}
          clientName={client.name}
          visibleTo={client.visible_to || []}
          onUpdate={refreshClients}
        />

        {client.status === "pending" ? (
          getStatusBadge(client.status)
        ) : (
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            <Key className="h-4 w-4 text-muted-foreground" />
            <Switch
              checked={client.status === "active"}
              onCheckedChange={(checked) => 
                handleStatusChange(client, checked ? "active" : "inactive")
              }
            />
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              handleClientClick(client.id);
            }}>
              Ver detalhes
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClient(client);
              }}
            >
              <Trash className="w-4 h-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
        <AddClientDialog onClientAdded={handleAddClient} />
      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <TabsList>
            <TabsTrigger value="all">Todos ({clients.length})</TabsTrigger>
            <TabsTrigger value="active">Ativos ({clients.filter(c => c.status === "active").length})</TabsTrigger>
            <TabsTrigger value="pending">Pendentes ({clients.filter(c => c.status === "pending").length})</TabsTrigger>
          </TabsList>
          
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle>Todos os clientes</CardTitle>
              <CardDescription>
                Gerencie todos os seus clientes e suas contas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-full bg-gray-200 animate-pulse"></div>
                        <div>
                          <div className="h-4 w-40 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-3 w-24 bg-gray-100 rounded mt-2 animate-pulse"></div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredClients.length > 0 ? (
                    filteredClients.map(renderClientCard)
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-muted-foreground">
                        Nenhum cliente encontrado com esses critérios
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Clientes ativos</CardTitle>
              <CardDescription>
                Clientes com contas ativas na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!isLoading && filteredClients
                  .filter((client) => client.status === "active")
                  .map(renderClientCard)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Clientes pendentes</CardTitle>
              <CardDescription>
                Clientes em processo de onboarding ou com pagamento pendente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!isLoading && filteredClients
                  .filter((client) => client.status === "pending")
                  .map(renderClientCard)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setClientToDelete(null);
          }
        }}
        title="Confirma a exclusão do cliente?"
        description={
          clientToDelete
            ? `Esta ação excluirá definitivamente o cliente "${clientToDelete.name}" e todos os dados associados. Esta operação não pode ser desfeita.`
            : ""
        }
        onConfirm={confirmDeleteClient}
        loading={isDeleting}
      />
    </div>
  );
}
